import { bookingFeeKindLabel, type FeeToConfirm } from "@teamna/shared";
import { Link } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import { copyForError } from "@teamna/web-client/api/copy.ts";
import { useDecideFee, useFeesToConfirm } from "@teamna/web-client/hooks/use-fees.ts";
import { useActiveBusiness } from "@teamna/web-client/hooks/use-owner.ts";
import { formatDateTime, formatMoney } from "@teamna/web-client/lib/format.ts";
import { Button, ErrorState, Notice, SkeletonList, showToast } from "../components/index.ts";
import { PayLinkPanel } from "../owner/parent/panels.tsx";

/**
 * No-show and late-cancel fees on the owner's To do list (Pilates studio shape I2). Each fee shows
 * who, why, the session and the amount, with Send (a one-off payment request with a card link,
 * which opens the link to copy or send on WhatsApp) and Waive (nothing is sent). Nothing goes out
 * by itself. The owner and the admins only: the API refuses everybody else.
 */
export function FeesToConfirmPanel({
  businessId,
  timezone,
  hold,
}: {
  businessId: string;
  timezone: string;
  /** The To do card keeps the link panel after the row leaves the list (Send takes the last fee off). */
  hold: (view: ReactNode | null) => void;
}) {
  const fees = useFeesToConfirm(businessId);
  const decide = useDecideFee(businessId);
  const { business } = useActiveBusiness();
  const [busy, setBusy] = useState<string | null>(null);

  if (!fees.data) return fees.isLoading ? <SkeletonList rows={1} /> : <ErrorState error={fees.error} onRetry={() => void fees.refetch()} />;

  function run(fee: FeeToConfirm, decision: "send" | "waive") {
    setBusy(`${fee.id}:${decision}`);
    decide.mutate(
      { feeId: fee.id, decision },
      {
        onSuccess: (answer) => {
          if (decision === "waive") showToast(`Fee waived for ${fee.participantName}`);
          else if (answer.payLink) hold(feeLinkPanel(fee, answer.payLink, business?.name ?? "", () => hold(null)));
          else showToast(`Fee sent to ${fee.memberName ?? fee.participantName}`);
        },
        onError: (error) => showToast(copyForError(error).message),
        onSettled: () => setBusy(null),
      },
    );
  }

  return <FeesToConfirmView fees={fees.data.items} canSend={fees.data.canSend} timezone={timezone} busy={busy} onDecide={run} />;
}

/** The link of a sent fee, to copy or send on WhatsApp. */
function feeLinkPanel(fee: FeeToConfirm, url: string, businessName: string, onClose: () => void): ReactNode {
  return (
    <PayLinkPanel
      items={[{ key: fee.id, what: `the ${bookingFeeKindLabel(fee.kind).toLowerCase()} fee`, amountFils: fee.amountFils, url }]}
      memberName={fee.memberName ?? fee.participantName}
      businessName={businessName}
      title="Fee sent"
      onClose={onClose}
    />
  );
}

/** The list itself, from plain props, so Storybook draws every state. */
export function FeesToConfirmView({
  fees,
  canSend,
  timezone,
  busy = null,
  onDecide,
}: {
  fees: FeeToConfirm[];
  canSend: boolean;
  timezone: string;
  /** `<feeId>:send` or `<feeId>:waive` while that write runs. */
  busy?: string | null;
  onDecide: (fee: FeeToConfirm, decision: "send" | "waive") => void;
}) {
  if (fees.length === 0) return <p className="text-[13.5px] text-ink-2">No fee waits for you.</p>;
  return (
    <div className="flex flex-col gap-3">
      <p className="text-[13.5px] text-ink-2">
        Teamna sends nothing by itself. Send makes a payment request with a card link. Waive lets the fee go. The session is taken in both cases.
      </p>
      {canSend ? null : (
        <Notice tone="warning" title="Card payments are not on yet">
          To send a fee, set up card payments on{" "}
          <Link to="/owner/setup/get-paid" className="font-semibold underline">
            Get paid
          </Link>
          . You can waive a fee now.
        </Notice>
      )}
      <ul aria-label="Fees to confirm" className="overflow-hidden rounded-card border border-line bg-paper [&>li:first-child]:border-t-0">
        {fees.map((fee) => (
          <li key={fee.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-line px-4 py-3">
            <div className="min-w-0 flex-1">
              <p className="text-[15px] font-semibold text-ink">
                <span dir="auto">{fee.participantName}</span>
                <span className="font-normal text-ink-2">, {bookingFeeKindLabel(fee.kind).toLowerCase()}</span>
              </p>
              <p className="text-[13.5px] text-ink-2">
                <span dir="auto">{fee.groupName}</span>, {formatDateTime(fee.startsAt, timezone)}
              </p>
            </div>
            <span className="text-[15px] font-semibold tabular-nums text-ink">{formatMoney(fee.amountFils)}</span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="secondary"
                disabled={!canSend || busy !== null}
                loading={busy === `${fee.id}:send`}
                aria-label={`Send the fee of ${formatMoney(fee.amountFils)} to ${fee.participantName}`}
                onClick={() => onDecide(fee, "send")}
              >
                Send
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={busy !== null}
                loading={busy === `${fee.id}:waive`}
                aria-label={`Waive the fee of ${fee.participantName}`}
                onClick={() => onDecide(fee, "waive")}
              >
                Waive
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
